import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Zap } from "lucide-react";

const hybridSolution = {
  label: "Recommended solution",
  title: "Hybrid system (PV + battery)",
  summary:
    "Built for long-term savings, stronger backup resilience, and better control of household energy use.",
  points: [
    "Combines solar generation with battery backup while remaining grid-connected.",
    "Stores excess daytime energy for evening use and better self-consumption.",
    "Keeps key circuits running during outages for stronger energy independence.",
  ],
  bestFor: [
    "Families with daytime and evening electricity demand",
    "Homes in outage-prone areas",
    "Owners focused on long-term bill reduction",
  ],
};

export function Solutions() {
  return (
    <section id="solutions" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-sm font-medium text-primary uppercase tracking-widest">
            Solutions
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">Our energy setup.</h2>
          <p className="mt-4 text-muted-foreground text-lg">
            We recommend one high-performance configuration that balances savings, reliability, and
            future-ready energy independence.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="relative mt-14 overflow-hidden rounded-3xl border border-primary/30 bg-primary/95 text-primary-foreground shadow-glow"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-background/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-background/15 blur-3xl" />

          <div className="relative grid gap-8 p-8 sm:p-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-10 lg:p-12">
            <div>
              <div className="inline-flex items-center rounded-sm bg-foreground px-3 py-1 text-xs font-semibold uppercase tracking-wider text-background">
                {hybridSolution.label}
              </div>
              <h3 className="mt-5 max-w-xl text-3xl leading-tight font-semibold sm:text-4xl">
                {hybridSolution.title}
              </h3>
              <p className="mt-4 max-w-2xl text-primary-foreground/95 text-lg">
                {hybridSolution.summary}
              </p>

              <div className="mt-8 space-y-5 text-base/8 max-w-2xl">
                {hybridSolution.points.map((point) => (
                  <p key={point} className="text-primary-foreground/95">
                    {point}
                  </p>
                ))}
              </div>

              <a
                href="https://ceb.mu/projects/solar-photovoltaic-scheme-for-households-2026"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 block rounded-md border border-foreground/35 bg-background/95 p-4 text-foreground hover:shadow-elegant transition"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-primary-foreground">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">
                      Eligible for the CEB household PV scheme
                    </p>
                    <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-4">
                      View scheme details
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </a>
            </div>

            <div className="rounded-2xl border border-border/80 bg-card/95 p-6 text-card-foreground shadow-elegant sm:p-7">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Best fit for
              </p>
              <ul className="mt-5 space-y-4">
                {hybridSolution.bestFor.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary">
                      <BadgeCheck className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm text-foreground/90 sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 rounded-xl border border-border bg-background/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Why this setup
                </p>
                <p className="mt-2 text-sm leading-6 text-foreground/90">
                  It helps you use more of your own solar energy, depend less on peak-time grid
                  tariffs, and stay powered during interruptions.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="mt-8 flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/10 px-5 py-4"
        >
          <BadgeCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <p className="font-medium text-base sm:text-lg">
            Any excess solar production can be exported to the grid, helping lower your electricity
            bill over time.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
